import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { Button } from "../components/Button";
import { TextField } from "../components/TextField";
import { errorMessage } from "../api/client";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? "/templates";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, from, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await login({ email, password });
      if (res.must_change_password) {
        // Restricted token in place — carry the just-typed password so the
        // set-password screen can call change-password without re-prompting.
        navigate("/set-password", { replace: true, state: { currentPassword: password } });
        return;
      }
      toast.success(t("auth.signedIn"));
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <form className="auth-card stack" onSubmit={onSubmit}>
        <h1>{t("auth.appName")}</h1>
        <p className="muted" style={{ textAlign: "center", marginTop: "-8px" }}>
          {t("auth.signInSubtitle")}
        </p>
        <TextField
          label={t("auth.email")}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.emailPlaceholder")}
        />
        <TextField
          label={t("auth.password")}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("auth.passwordPlaceholder")}
        />
        <Button type="submit" variant="primary" loading={submitting} disabled={!email || !password}>
          {t("auth.signIn")}
        </Button>
        <p className="faint" style={{ textAlign: "center", margin: 0 }}>
          <Link to="/forgot-password">{t("auth.forgotPassword")}</Link>
        </p>
      </form>
    </div>
  );
}
